import { HttpRequest } from "@angular/common/http";
import { PerformanceMockDb } from "./performance-mock.db";

/**
 * Tabla de rutas del simulador de API del módulo de desempeño.
 *
 * Cada ruta declara su método, un patrón de path (relativo a PERFORMANCE_API_BASE,
 * con segmentos `:param`) y un handler que lee/muta la {@link PerformanceMockDb} y
 * devuelve el body de la respuesta. Es la "tabla de contrato": agregar un endpoint
 * nuevo = agregar una fila aquí.
 */

/** Body de respuesta mock (la forma cruda que esperan hoy las vistas). */
export type MockResponseBody = any;

export interface MockRoute {
	method: "GET" | "POST" | "PUT" | "DELETE";
	/** Patrón de path relativo a la base, ej. "v1/app/:ws/:sws/processes/:id/objectives". */
	pattern: string;
	handler: (ctx: MockRouteContext) => MockResponseBody;
}

export interface MockRouteContext {
	req: HttpRequest<any>;
	/** Parámetros de path extraídos del patrón (ej. { id: "1", goalId: "2" }). */
	params: Record<string, string>;
	/** Parámetros de query (?memberId=...). */
	query: URLSearchParams;
	db: PerformanceMockDb;
}

/**
 * Lee el body del request, parseándolo si vino como string JSON (otros
 * interceptores lo serializan) o como `FormData` (los envíos de evaluación con
 * evidencias adjuntas viajan en multipart). Así los handlers del mock siguen
 * viendo siempre un objeto plano, sin saber cómo se codificó la petición.
 */
function parseBody(req: HttpRequest<any>): any {
	const body = req.body;
	if (typeof body === "string") {
		try {
			return JSON.parse(body);
		} catch {
			return body;
		}
	}
	if (body instanceof FormData) return formDataToObject(body);
	return body || {};
}

/**
 * Reconstruye el objeto anidado a partir de las claves con notación PHP que arma
 * {@link PerformanceModuleApiService} (`objectives[0][score]`,
 * `objectives[0][attachments][]`). Los archivos se reducen a su nombre: al mock le
 * basta con saber cuántos y cuáles llegaron.
 */
function formDataToObject(form: FormData): any {
	const result: any = {};

	form.forEach((value, rawKey) => {
		const [head, ...rest] = rawKey.split("[").map((part) => part.replace("]", ""));
		const path = [head, ...rest];
		const item = value instanceof File ? value.name : value;

		let node = result;
		path.forEach((segment, index) => {
			const isLast = index === path.length - 1;
			const nextIsIndex = !isLast && (path[index + 1] === "" || /^\d+$/.test(path[index + 1]));

			// Segmento vacío = elemento de una colección (`attachments[]`): se acumula.
			if (segment === "") {
				if (!Array.isArray(node)) return;
				node.push(item);
				return;
			}
			if (isLast) {
				node[segment] = item;
				return;
			}
			if (node[segment] === undefined) node[segment] = nextIsIndex ? [] : {};
			node = node[segment];
		});
	});

	return result;
}

/** Tabla de rutas. El orden importa: gana la primera que matchee método + path. */
export const PERFORMANCE_MOCK_ROUTES: MockRoute[] = [
	// ---- Procesos ----------------------------------------------------------
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/home",
		handler: ({ db }) => db.getPerformanceHome(),
	},
	// Home: resumen global del usuario (métricas, siguiente actividad, organización).
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/profile",
		handler: ({ db }) => db.getProfile(),
	},
	// Home: procesos recientes con su avance por etapa (bloque "Avance del proceso").
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/processes/latest",
		handler: ({ db, query }) => db.getLatestProcesses(query.get("limit"), query.get("active") === "1"),
	},
	// Home: anuncios ("Novedades") del subworkspace, endpoint propio de desempeño.
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/announcements",
		handler: ({ db, query }) => db.getAnnouncements(query.get("page"), query.get("per_page")),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes",
		handler: ({ db }) => db.listProcesses(),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/show",
		handler: ({ db, params }) => db.getProcess(params.id),
	},
	// Análisis de avance de IsA: general del proceso o acotado a una etapa (`stage_id`).
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/isa-analysis",
		handler: ({ db, params, req }) => db.getIsaAnalysis(params.id, parseBody(req)),
	},
	// Mismo análisis pero de un evaluado a cargo del líder (`reviewee_id` en el body).
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/isa-analysis",
		handler: ({ db, params, req }) => db.getTeamIsaAnalysis(params.id, parseBody(req)),
	},

	// ---- Objetivos ---------------------------------------------------------
  // personal
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/get-form-data",
		handler: ({ db }) => db.getObjectiveFormData(),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/isa-suggestion",
		handler: ({ db }) => db.getObjectiveIsaSuggestion(),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives",
		handler: ({ db, params, query }) => db.listPersonalObjectives(params.id, query.get("code")),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objective-reviews/store",
		handler: ({ db, params, req }) => db.submitObjectiveReviews(params.id, parseBody(req)),
	},
  {
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/store",
		handler: ({ db, req }) => db.addObjective(parseBody(req)),
	},
  {
		method: "PUT",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:goalId/update",
		handler: ({ db, params, req }) => db.updateObjective(params.goalId, parseBody(req)),
	},
  {
		method: "DELETE",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:goalId/delete",
		handler: ({ db, params }) => ({ id: params.goalId, deleted: db.deleteObjective(params.goalId) }),
	},
  {
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:objectiveId/tracking/list",
		handler: ({ db, params, query }) =>
			db.listObjectiveTrackings(params.id, params.objectiveId, query.get("page"), query.get("per_page")),
	},
  {
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:objectiveId/tracking/store",
		handler: ({ db, params, req }) => db.trackObjective(params.id, params.objectiveId, parseBody(req)),
	},
  {
		method: "PUT",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:objectiveId/tracking/:trackingId/update",
		handler: ({ db, params, req }) =>
			db.updateObjectiveTracking(params.objectiveId, params.trackingId, parseBody(req)),
	},
  {
		// Mismo update, pero con evidencias: viaja como POST con `_method=PUT` porque
		// PHP no parsea el cuerpo multipart de un PUT.
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:objectiveId/tracking/:trackingId/update",
		handler: ({ db, params, req }) =>
			db.updateObjectiveTracking(params.objectiveId, params.trackingId, parseBody(req)),
	},
  {
		method: "DELETE",
		pattern: "v1/app/:ws/:sws/me/processes/:id/objectives/:objectiveId/tracking/:trackingId/delete",
		handler: ({ db, params }) => db.deleteObjectiveTracking(params.objectiveId, params.trackingId),
	},

	// ---- Seguimiento del equipo (rol líder) --------------------------------
	{
		// Listado "Seguimiento de objetivos - Mi equipo" (vista 1 de la etapa).
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/trackings/summary",
		handler: ({ db, params }) => db.getTeamTrackingTeamSummary(params.id),
	},
	{
		// Seguimiento de una persona (vista 2): avance e historial por objetivo.
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/reviewees/:revieweeId/trackings",
		handler: ({ db, params }) => db.getRevieweeTrackings(params.id, params.revieweeId),
	},
	{
		// El registro de avance del líder, con la misma forma que la API real.
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/reviewees/:revieweeId/objectives/:objectiveId/tracking/store",
		handler: ({ db, params, req }) =>
			db.trackTeamObjective(params.id, params.revieweeId, params.objectiveId, parseBody(req)),
	},
	{
		// El diseño final ya no muestra niveles de riesgo; se conserva porque el
		// endpoint está publicado.
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/trackings/risk",
		handler: ({ db, params }) => db.getTeamTrackingRisk(params.id),
	},
	{
		// `forced=1` regenera el resumen aunque nada haya cambiado.
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/trackings/isa-summary",
		handler: ({ db, params, query }) =>
			db.getTeamTrackingIsaSummary(params.id, query.get("forced") === "1"),
	},
  //equipo
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/get-form-data",
		handler: ({ db }) => db.getObjectiveFormData(),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/store",
		handler: ({ db, req }) => db.addTeamObjective(parseBody(req)),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/isa-suggestion",
		handler: ({ db, req }) => db.getTeamObjectiveIsaSuggestion(parseBody(req)),
	},
	{
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/:objectiveId/update",
		handler: ({ db, params, req }) => db.updateTeamObjective(params.objectiveId, parseBody(req)),
	},
	{
		method: "DELETE",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/:objectiveId/delete",
		handler: ({ db, params }) => db.deleteTeamObjective(params.objectiveId),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/:objectiveId/approve",
		handler: ({ db, params, req }) => db.approveTeamObjective(params.objectiveId, parseBody(req)),
	},
	{
		// Aprobación masiva (endpoint real: PUT team/processes/:id/objectives/update-approval, B5b).
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/update-approval",
		handler: ({ db, req }) => db.approveTeamObjectivesMany(parseBody(req)),
	},
	{
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives/:objectiveId/review",
		handler: ({ db, params, req }) => db.reviewTeamObjective(params.objectiveId, parseBody(req)),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/objectives",
		handler: ({ db, params, query }) =>
			db.listTeamMemberObjectives(params.id, query.get("reviewee_id"), query.get("page")),
	},


	// ---- Equipo (Mi equipo) ------------------------------------------------
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/reviewees",
		handler: ({ db, params, query }) =>
			db.listTeamReviewees(params.id, {
				q: query.get("q"),
				status: query.get("status"),
				page: query.get("page"),
				perPage: query.get("per_page"),
			}),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/reviewees",
		handler: ({ db, params, query }) =>
			db.listEvaluationReviewees(params.id, {
				q: query.get("q"),
				status: query.get("status"),
				page: query.get("page"),
				perPage: query.get("per_page"),
			}),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/processes/:id/team/approve",
		handler: ({ db, req }) => {
			const body = parseBody(req);
			const memberIds = Array.isArray(body?.memberIds) ? body.memberIds : [].concat(body?.memberIds ?? []);
			return db.approveTeamGoals(memberIds);
		},
	},

	// ---- Evaluación de competencias ----------------------------------------
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/processes/:id/evaluation/team",
		handler: ({ db, params }) => db.getEvaluationTeam(params.id),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/processes/:id/evaluation/competencies",
		handler: ({ db, query }) => db.getCompetencyEvaluation(query.get("personId")),
	},
	// autoevaluación: competencias del proceso a autoevaluar
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/competencies",
		handler: ({ db, params }) => db.getSelfAssessmentCompetencies(params.id),
	},
	// autoevaluación: detalle de la evaluación ya realizada (si existe)
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/competency-reviews/show",
		handler: ({ db, params }) => db.getSelfAssessmentReview(params.id),
	},
	// autoevaluación: envío de la calificación de competencias
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/competency-reviews/store",
		handler: ({ db, params, req }) => db.submitSelfAssessment(params.id, parseBody(req)),
	},
	// autoevaluación: actualización de una evaluación ya enviada
	{
		method: "PUT",
		pattern: "v1/app/:ws/:sws/me/processes/:id/competency-reviews/update",
		handler: ({ db, params, req }) => db.updateSelfAssessment(params.id, parseBody(req)),
	},

	// ---- PDI (Plan de Desarrollo Individual) -------------------------------
	// Backend real desde el cambio `app-pdi-leader-team`: sin filas de mock.

	// ---- Feedback (vista del líder) ----------------------------------------
	// Todas las rutas usan el patrón **real** de la API v2 y cuelgan de
	// `team/processes/:id/feedbacks`: al apagar el mock, cada llamada ya pega
	// donde debe.
	//
	// Las de segmento fijo (`/filters`, `/scheduling-options`, `/store`,
	// `/points/preview`) se listan primero por legibilidad, no por precedencia:
	// `patternToRegExp` ancla el patrón completo, así que un `:feedbackId` nunca
	// puede tragarse una ruta de distinta forma.
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/filters",
		handler: ({ db }) => db.getFeedbackFilters(),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/scheduling-options",
		handler: ({ db }) => db.getFeedbackSchedulingOptions(),
	},
	{
		// Listado del equipo, filtrado y paginado server-side.
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks",
		handler: ({ db, query }) =>
			db.getFeedbackTeamRows(query.get("page"), query.get("per_page") ?? query.get("rowsPerPage"), {
				q: query.get("filters[q]"),
				session: query.get("filters[session]"),
				approval: query.get("filters[approval]"),
				status: query.get("filters[status]"),
				criteria: query.get("filters[criteria]"),
			}),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/store",
		handler: ({ db, req }) => db.saveFeedbackMeeting(parseBody(req)),
	},
	{
		// Guía propuesta por ISA. No guarda nada: se puede pedir las veces que haga
		// falta, con `reviewee_id` (antes de agendar) o con `feedback_id`.
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/points/preview",
		handler: ({ db }) => db.previewFeedbackPoints(),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/show",
		handler: ({ db, params }) => db.getFeedbackDetail(params.feedbackId),
	},
	{
		// Inicio y cierre de la reunión. Los dos son idempotentes y devuelven la
		// sesión completa, igual que el endpoint real.
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/start",
		handler: ({ db, params }) => db.startFeedbackSession(params.feedbackId),
	},
	{
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/finish",
		handler: ({ db, params }) => db.finishFeedbackSession(params.feedbackId),
	},
	{
		// Guardado de la guía: reemplaza los puntos y persiste las notas por bloque.
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/points",
		handler: ({ db, params, req }) => db.saveFeedbackPoints(params.feedbackId, parseBody(req)),
	},
	{
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/points/:pointId/check",
		handler: ({ db, params, req }) =>
			db.setFeedbackPointChecked(params.feedbackId, params.pointId, parseBody(req)?.checked),
	},
	{
		// Subida del audio de la reunión. A diferencia del resto, el body es un
		// `FormData` real (multipart), así que no pasa por `parseBody`.
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/recording",
		handler: ({ db, params, req }) =>
			db.saveFeedbackRecording(params.feedbackId, req.body as FormData),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/feedbacks/:feedbackId/recording",
		handler: ({ db, params }) => db.getFeedbackRecording(params.feedbackId),
	},

	// ---- Feedback (vista del colaborador) ----------------------------------
	// Las dos rutas usan el patrón **real** del backend: al apagar el mock, estas
	// llamadas ya pegan donde deben.
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/feedback",
		handler: ({ db }) => db.getMyFeedback(),
	},
	{
		// Conformidad del evaluado (sin `feedback_id`: la sesión la resuelve el
		// usuario autenticado, igual que el GET de arriba).
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/feedbacks/accept",
		handler: ({ db, req }) => db.acceptMyFeedback(parseBody(req)),
	},

	// ---- Calibración (etapa "calibration", rol líder) ----------------------
	// TODO (backend): rutas mock provisionales; la etapa completa está pendiente
	// de publicar.
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations",
		handler: ({ db, params }) => db.getCalibrationTeam(params.id),
	},
	{
		// Catálogo de las nueve cajas de la matriz 9-box (llegan aunque estén
		// vacías: es lo que permite pintar la matriz completa).
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations/boxes",
		handler: ({ db, params }) => db.getCalibrationNineBoxes(params.id),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations/show",
		handler: ({ db, query }) => db.getCalibrationDetail(query.get("reviewee_id")),
	},
	{
		// Guardado de un ítem calibrado. Puede llegar como JSON o como multipart
		// (cuando el líder adjuntó evidencias): `parseBody` normaliza ambos.
		method: "PUT",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations/items",
		handler: ({ db, req }) => db.saveCalibrationItem(parseBody(req)),
	},
	{
		// El body es un `FormData` real (multipart), así que no pasa por `parseBody`.
		method: "POST",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations/recording",
		handler: ({ db, req }) => db.saveCalibrationRecording(req.body as FormData),
	},
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/team/processes/:id/calibrations/recording",
		handler: ({ db, query }) => db.getCalibrationRecording(query.get("reviewee_id")),
	},

	// ---- Encuesta del proceso (etapa "survey") -----------------------------
	// TODO (backend): rutas mock provisionales, a reemplazar por el contrato real.
	{
		method: "GET",
		pattern: "v1/app/:ws/:sws/me/processes/:id/survey",
		handler: ({ db, params }) => db.getProcessSurvey(params.id),
	},
	{
		method: "POST",
		pattern: "v1/app/:ws/:sws/me/processes/:id/survey/store",
		handler: ({ db, req }) => db.submitProcessSurvey(parseBody(req)),
	},
];

/** Compila un patrón ("a/:id/b") a regex con grupos nombrados por cada `:param`. */
function patternToRegExp(pattern: string): { regex: RegExp; keys: string[] } {
	const keys: string[] = [];
	const source = pattern
		.split("/")
		.map((segment) => {
			if (segment.startsWith(":")) {
				keys.push(segment.slice(1));
				return "([^/]+)";
			}
			return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		})
		.join("/");
	return { regex: new RegExp(`^${source}$`), keys };
}

/**
 * Busca la ruta que matchea el método + path relativo. Devuelve la ruta y los
 * params extraídos, o null si ninguna matchea.
 */
export function matchRoute(
	method: string,
	relativePath: string,
): { route: MockRoute; params: Record<string, string> } | null {
	for (const route of PERFORMANCE_MOCK_ROUTES) {
		if (route.method !== method) continue;
		const { regex, keys } = patternToRegExp(route.pattern);
		const match = regex.exec(relativePath);
		if (!match) continue;
		const params: Record<string, string> = {};
		keys.forEach((key, i) => (params[key] = decodeURIComponent(match[i + 1])));
		return { route, params };
	}
	return null;
}
